/**
 * Bulk image drop-in: images named like the scenes (S01.png, s1.jpg, scene-35.png …) replace the
 * import placeholders automatically, keeping the scene's timing and any edits.
 *
 * In a build sequence the step's image is a full picture of the scene so far. It is re-analysed
 * against the previous step's image ("diffAgainst"), so only what changed is drawn, and laid
 * exactly over the previous image.
 */

import { sortedScenes, type Element, type Project } from "@studio/engine";
import type { Draft } from "immer";
import { api, type AnalysisState } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";

const BOX = { x: 0.03 * 1920, y: 0.07 * 1080, w: 0.6 * 1920, h: 0.86 * 1080 }; // left two-thirds

type Stage = "waiting" | "diffing";
const queue = new Map<string, { num: string; stage: Stage; target?: string }>();
let busy = false;

export function sceneNumberOf(name: string): string | null {
  const m = name.match(/^(?:s|scene)[\s_-]*0*(\d+)[a-z]?\.[a-z0-9]+$/i);
  return m ? m[1] : null;
}

/** Queue image assets for automatic placement (called after uploads). */
export function queueAutoPlace(assetIds: string[]) {
  const p = useEditor.getState().project;
  if (!p) return;
  for (const id of assetIds) {
    const a = p.assets.find((x) => x.id === id);
    const num = a && (a.kind === "image" || a.kind === "svg") ? sceneNumberOf(a.name) : null;
    if (num) queue.set(id, { num, stage: "waiting" });
  }
  void pump();
}

/** Queue one specific image element to be re-drawn as "only what's new" in its build step. */
export function queueDiffFor(elementId: string) {
  const p = useEditor.getState().project;
  const el = p?.elements.find((e) => e.id === elementId);
  if (!el?.image) return;
  queue.set(el.image.assetId, { num: "", stage: "waiting", target: elementId });
  void pump();
}

/** Matches between uploaded images and placeholders still waiting for one. */
export function placeholderMatches(p: Project): { assetId: string; num: string }[] {
  const out = [];
  for (const a of p.assets) {
    const num = a.kind === "image" || a.kind === "svg" ? sceneNumberOf(a.name) : null;
    if (num && findPlaceholder(p, num)) out.push({ assetId: a.id, num });
  }
  return out;
}

export function pendingCount(): number {
  return queue.size;
}

function findPlaceholder(p: Project, num: string): Element | undefined {
  return p.elements.find((e) => e.type === "text" && e.name === `Placeholder S${num}`);
}

/**
 * The image element of the previous step in a build chain (walking back through build scenes).
 * "wait" while a nearer step's placeholder is still about to receive its own image.
 */
function previousStepImage(p: Project, sceneId: string): Element | "wait" | undefined {
  const scenes = sortedScenes(p);
  let i = scenes.findIndex((s) => s.id === sceneId);
  if (i <= 0 || scenes[i].startMode !== "build") return undefined;
  const queuedNums = new Set([...queue.values()].map((q) => q.num));
  while (i > 0) {
    i--;
    const img = p.elements.filter((e) => e.sceneId === scenes[i].id && e.type === "image").sort((a, b) => b.start - a.start)[0];
    if (img) return img;
    const ph = p.elements.find((e) => e.sceneId === scenes[i].id && e.name.startsWith("Placeholder S"));
    if (ph && queuedNums.has(ph.name.replace("Placeholder S", ""))) return "wait";
    if (scenes[i].startMode !== "build") return undefined;
  }
  return undefined;
}

/** Process the queue; re-run whenever analysis results change. */
export async function pump(): Promise<void> {
  if (busy) return;
  busy = true;
  try {
    for (const [assetId, item] of [...queue]) {
      const st = useEditor.getState();
      const p = st.project;
      if (!p) return;
      const asset = p.assets.find((a) => a.id === assetId);
      if (!asset) {
        queue.delete(assetId);
        continue;
      }
      const target = item.target ? p.elements.find((e) => e.id === item.target) : findPlaceholder(p, item.num);
      if (!target) {
        queue.delete(assetId);
        continue;
      }
      const prevStep = previousStepImage(p, target.sceneId);
      if (prevStep === "wait") continue;
      const prev = prevStep;
      const prevAsset = prev?.image ? p.assets.find((a) => a.id === prev.image!.assetId) : undefined;
      const an = st.analysis[assetId];
      if (prev && prevAsset) {
        const prevAn = st.analysis[prevAsset.id];
        if (!prevAn?.ready) continue; // previous step still analysing
        const wanted = prevAsset.file;
        if (item.stage === "waiting") {
          if (an?.ready && an.params?.diffAgainst === wanted) {
            place(target.id, assetId, an, prev, prevAn);
            queue.delete(assetId);
            continue;
          }
          if (an?.job && ["queued", "running"].includes(an.job.status)) continue; // first analysis still running
          // switch the step's analysis to "only what's new" and run it
          st.edit((d) => {
            const a = d.assets.find((x) => x.id === assetId);
            if (a?.analysis) a.analysis.params.diffAgainst = wanted;
          });
          await flush();
          const params = useEditor.getState().project!.assets.find((x) => x.id === assetId)!.analysis!.params;
          await api.analyze(p.id, assetId, params);
          item.stage = "diffing";
          window.dispatchEvent(new Event("studio:poll"));
          continue;
        }
        const idle = !(an?.job && ["queued", "running"].includes(an.job.status));
        if (an?.ready && an.params?.diffAgainst === wanted && idle) {
          place(target.id, assetId, an, prev, prevAn);
          queue.delete(assetId);
        } else if (an?.ready && idle && an.params?.diffAgainst !== wanted) {
          item.stage = "waiting"; // the previous step changed since: compare again
        }
        if (an?.job?.status === "error") queue.delete(assetId);
        continue;
      }
      if (an?.ready && !(an.job && ["queued", "running"].includes(an.job.status))) {
        place(target.id, assetId, an);
        queue.delete(assetId);
      } else if (an?.job?.status === "error") queue.delete(assetId);
    }
  } finally {
    busy = false;
    // later steps wait on earlier ones (placed, analysed): keep checking while anything is queued
    window.clearTimeout(retry);
    if (queue.size > 0) retry = window.setTimeout(() => void pump(), 1500);
  }
}

let retry: number | undefined;

/** Turn the target (placeholder or image) into the image, placed and timed. */
function place(targetId: string, assetId: string, an: AnalysisState, prev?: Element, prevAn?: AnalysisState) {
  const st = useEditor.getState();
  st.edit((d) => {
    const el = d.elements.find((e) => e.id === targetId);
    if (!el) return;
    const wasPlaceholder = el.type === "text";
    el.type = "image";
    el.text = null;
    el.name = el.name.replace(/^Placeholder /, "") + (wasPlaceholder ? " image" : "");
    el.image = { assetId, brushScale: el.image?.brushScale ?? 1, groupOrder: null };
    el.z = Math.max(el.z, 1);
    const [lw, lh] = an.size ?? [1000, 1000];
    if (prev && prevAn?.crop && an.crop && prevAn.source && an.source) {
      // lay the new layer exactly over the previous step's picture
      const k = prevAn.source[0] / an.source[0];
      const s = prev.transform.scale * k;
      const [ax, ay, aw, ah] = prevAn.crop;
      const [bx, by, bw, bh] = an.crop;
      const ox = ((bx + bw / 2) * k - (ax + aw / 2)) * prev.transform.scale;
      const oy = ((by + bh / 2) * k - (ay + ah / 2)) * prev.transform.scale;
      const r = (prev.transform.rotation * Math.PI) / 180;
      el.transform = {
        ...prev.transform,
        scale: +s.toFixed(5),
        x: +(prev.transform.x + ox * Math.cos(r) - oy * Math.sin(r)).toFixed(2),
        y: +(prev.transform.y + ox * Math.sin(r) + oy * Math.cos(r)).toFixed(2),
      };
    } else if (wasPlaceholder) {
      const sc = Math.min(BOX.w / lw, BOX.h / lh);
      el.transform = { ...el.transform, x: BOX.x + BOX.w / 2, y: BOX.y + BOX.h / 2, scale: +sc.toFixed(4), rotation: 0 };
    }
    if (wasPlaceholder) fitScene(d, el);
  });
}

/** Draw the new image in the time the scene has, and start the scene's text after it. */
function fitScene(d: Draft<Project>, img: Draft<Element>) {
  const scenes = sortedScenes(d as Project);
  const i = scenes.findIndex((s) => s.id === img.sceneId);
  const end = i + 1 < scenes.length ? scenes[i + 1].start : (d.duration ?? scenes[i].start + 6);
  const D = end - scenes[i].start;
  const lead = img.start;
  const text = d.elements.find((e) => e.sceneId === img.sceneId && e.type === "text" && e.id !== img.id && !e.name.startsWith("Placeholder"));
  const txtDur = text ? text.enter.outlineDur : 0;
  const hold = Math.min(1.5, 0.3 * D);
  const avail = Math.max(0.6, D - lead - hold - (text ? txtDur + 0.15 : 0));
  const total = Math.max(0.7, Math.min(4.4, avail));
  img.enter = { ...img.enter, mode: "draw", outlineDur: +(total * 0.62).toFixed(3), fillDur: +(total * 0.38).toFixed(3) };
  if (text) text.start = +(lead + total + 0.15).toFixed(3);
}
